import { Component, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Constants } from '../../../shared/interfaces/constants';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { AuthorComponent } from './author/author.component';
import { MediumComponent } from './medium/medium.component';
import { MatDividerModule } from '@angular/material/divider';
import { state, style, trigger } from '@angular/animations';
import { take } from 'rxjs/internal/operators/take';
import { NavMapComponent } from '../../../shared/components/nav-map/nav-map.component';
import { ConfirmationDialogComponent } from '../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { CommonModule } from '@angular/common';
import { UtilsService } from '../../../shared/services/utils.service';
import {
	ClientCategoryControllerService as ClippingClientCategoryControllerService,
	ManagementAuthorControllerService,
	ManagementAuthorResponse,
	ManagementCategoryClippingControllerService,
	ManagementCategoryResponse,
	ManagementClipControllerService,
	ManagementKeywordControllerService,
	ManagementKeywordResponse,
	ManagementMediumClippingControllerService,
	ManagementMediumFilterRequest,
	ManagementMediumResponse,
	ManagementTagClippingControllerService,
	ManagementTagResponse,
	SearchByKeywordRequest,
} from 'api-clipping';
import {
	ClientCategoryControllerService as ContractNoticesClientCategoryControllerService,
	ManagementBusinessEntityControllerService,
	ManagementBusinessEntityResponse,
	ManagementCategoryContractNoticesControllerService,
	ManagementMediumContractNoticesControllerService,
	ManagementTagContractNoticesControllerService,
} from 'api-contract-notices';
import {
	CategoryResponse as TranscriptionCategoryResponse,
	ManagementCategoryTranscriptionControllerService,
	ManagementTagTranscriptionControllerService,
	TagResponse as TranscriptionTagResponse,
} from 'api-transcription';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { TagComponent } from './tag/tag.component';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { KeywordComponent } from './keyword/keyword.component';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Subscription } from 'rxjs/internal/Subscription';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { BusinessEntityComponent } from './business-entity/business-entity';

interface ManagementCategoryWithTags extends ManagementCategoryResponse {
	tags?: ManagementTagResponse[];
	isOpened?: boolean;
}

@Component({
	selector: 'app-manage-data-items',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		TranslateModule,
		MatFormFieldModule,
		MatInputModule,
		MatIconModule,
		MatCardModule,
		MatButtonModule,
		MatDividerModule,
		MatTooltipModule,
		NavMapComponent,
		MatExpansionModule,
		MatTableModule,
		MatPaginatorModule,
		MatSelectModule,
		MatCheckboxModule,
	],
	templateUrl: './management.component.html',
	styleUrl: './management.component.scss',
	animations: [
		trigger('showHide', [
			state(
				'show',
				style({
					display: 'inline-block',
				})
			),
			state(
				'hide',
				style({
					display: 'none',
				})
			),
		]),
	],
})
export class ManagementComponent {
	private readonly DEBOUNCE_TIME = 300;
	@ViewChildren(MatPaginator) paginator = new QueryList<MatPaginator>();
	pageOptions = [50, 100, 200];
	@ViewChild('selectAllMediumTypes') private selectAllMediumTypes!: MatOption;

	@ViewChild('tagsAccordion', { static: false }) accordion: MatAccordion | undefined;
	itemType: 'AUTHOR' | 'MEDIUM' | 'TAG' | 'KEYWORD' | 'BUSINESS_ENTITY' = 'AUTHOR';
	items:
		| ManagementAuthorResponse[]
		| ManagementMediumResponse[]
		| ManagementCategoryWithTags[]
		| ManagementTagResponse[]
		| ManagementKeywordResponse[]
		| ManagementBusinessEntityResponse[] = [];
	itemTypeWithScope!: 'tag_clipping' | 'tag_contract_notices' | 'tag_transcriptions' | 'medium_clipping' | 'medium_contract_notices' | undefined;

	searchFormControl: FormControl = new FormControl();
	mediumTypeFormControl: FormControl = new FormControl([]);
	mediumLangFormControl: FormControl = new FormControl([]);
	$searchFormControlSubscription!: Subscription;
	$mediumTypeSubscription!: Subscription;
	$mediumLangSubscription!: Subscription;
	searchedTerm: string = '';
	foundResults: number = 0;

	clipTypes = Constants.CLIP_TYPES;
	langOptions: any[] = [];

	displayedColumns: string[] = [];
	dataSource!: MatTableDataSource<any>;
	private lockAction = false;

	activatedLetter = '';
	letterScriptTypeStore: 'latin' | 'cyrillic' | undefined;

	englishAlphabet = Constants.LANGUAGE_ALPHABETS.get('en');
	macedonianAlphabet = Constants.LANGUAGE_ALPHABETS.get('mk');
	displayDeleteIcon!: string;

	searchStore!: ManagementCategoryWithTags[]; // used for tags search

	// keyword list filters (client-side, applied on top of the already-loaded/searched items)
	keywordTagCategories: ManagementCategoryWithTags[] = [];
	keywordSources = Constants.KEYWORDS_SOURCE_TYPES;
	selectedKeywordTagIds: string[] = [];
	selectedKeywordSources: string[] = [];

	constructor(
		private route: ActivatedRoute,
		private dialog: MatDialog,
		public utils: UtilsService,
		private apiClippingAuthor: ManagementAuthorControllerService,
		private apiClippingMedium: ManagementMediumClippingControllerService,
		private apiContractNoticesMedium: ManagementMediumContractNoticesControllerService,
		private apiContractNoticeManagementCategory: ManagementCategoryContractNoticesControllerService,
		private apiClippingCategory: ManagementCategoryClippingControllerService,
		private apiClippingKeyword: ManagementKeywordControllerService,
		private apiContractNoticesBusinessEntity: ManagementBusinessEntityControllerService,
		private apiClippingClip: ManagementClipControllerService,
		private apiTranscriptionCategory: ManagementCategoryTranscriptionControllerService,
		private apiTranscriptionTag: ManagementTagTranscriptionControllerService
	) {
		this.route.params.pipe(takeUntilDestroyed()).subscribe(params => {
			this.itemType = Constants.DATA_ITEM_TYPES.get(params['type']) || 'AUTHOR';
			this.itemTypeWithScope = undefined;
			if (this.$searchFormControlSubscription) {
				this.$searchFormControlSubscription.unsubscribe();
			}
			if (this.$mediumTypeSubscription) {
				this.$mediumTypeSubscription.unsubscribe();
			}
			if (this.$mediumLangSubscription) {
				this.$mediumLangSubscription.unsubscribe();
			}
			this.searchFormControl.setValue('');
			this.activatedLetter = '';
			this.letterScriptTypeStore = undefined;
			this.items = [];
			this.selectedKeywordTagIds = [];
			this.selectedKeywordSources = [];

			switch (this.itemType) {
				case 'AUTHOR': {
					this.pageOptions = [200, 400, 600];
					if (this.paginator.toArray()[0]) {
						this.paginator.toArray()[0].pageIndex = 0;
						this.paginator.toArray()[0].pageSize = this.pageOptions[0];
					}
					// this.getDataByLetter('A', 'latin');
					this.getAuthors();
					break;
				}
				case 'MEDIUM': {
					this.itemTypeWithScope = params['type'];
					this.langOptions = this.itemTypeWithScope === 'medium_clipping' ? Constants.LANGUAGES_CLIPPING_MEDIUMS : Constants.COUNTRIES;
					this.pageOptions = [200, 400, 600];
					if (this.paginator.toArray()[0]) {
						this.paginator.toArray()[0].pageIndex = 0;
						this.paginator.toArray()[0].pageSize = this.pageOptions[0];
					}
					//this.getDataByLetter('A', 'latin');
					this.getMediums();
					break;
				}
				case 'TAG': {
					this.itemTypeWithScope = params['type'];
					this.getTagCategories();
					break;
				}
				case 'KEYWORD': {
					this.displayedColumns = ['TEXT', 'SOURCES', 'TAGS'];
					this.getAllKeywords();
					this.getKeywordTagCategories();
					break;
				}
				case 'BUSINESS_ENTITY': {
					this.pageOptions = [50, 100, 200];
					if (this.paginator.toArray()[0]) {
						this.paginator.toArray()[0].pageIndex = 0;
						this.paginator.toArray()[0].pageSize = this.pageOptions[0];
					}
					this.getBusinessEntities();
					break;
				}
			}

			this.$searchFormControlSubscription = this.searchFormControl.valueChanges.pipe(debounceTime(this.DEBOUNCE_TIME)).subscribe(searchTerm => {
				this.search();
			});

			this.$mediumTypeSubscription = this.mediumTypeFormControl.valueChanges.pipe(debounceTime(this.DEBOUNCE_TIME)).subscribe(() => {
				this.search();
			});

			this.$mediumLangSubscription = this.mediumLangFormControl.valueChanges.pipe(debounceTime(this.DEBOUNCE_TIME)).subscribe(() => {
				this.search();
			});
			
			this.mediumTypeFormControl.setValue([]);
			this.mediumLangFormControl.setValue([]);
		});
	}

	itemAction(
		action: 'NEW' | 'EDIT' | 'DELETE',
		item?: ManagementAuthorResponse | ManagementMediumResponse | ManagementCategoryResponse | ManagementTagResponse | ManagementKeywordResponse | ManagementBusinessEntityResponse,
		subtype?: 'CATEGORY' | 'TAG'
	) {
		if (this.lockAction) {
			return;
		}
		this.lockAction = true;

		let component: any;
		let preloadedData;
		switch (this.itemType) {
			case 'AUTHOR': {
				component = AuthorComponent;
				break;
			}
			case 'MEDIUM': {
				component = MediumComponent;
				break;
			}
			case 'TAG': {
				component = TagComponent;
				if (action === 'NEW' && !subtype) {
					// send categoryId for NEW TAG
					preloadedData = {
						categoryId: item ? item.id : null,
					};
				}
				break;
			}
			case 'KEYWORD': {
				component = KeywordComponent;
				break;
			}
			case 'BUSINESS_ENTITY': {
				component = BusinessEntityComponent;
				break;
			}
		}

		const dialogRef = this.dialog.open(component, {
			width: '600px',
			data: {
				mode: action,
				type: subtype ? subtype : this.itemType,
				typeWithScope: this.itemTypeWithScope,
				item,
				additionalData: this.items,
				preloadedData,
			},
		});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(result => {
				this.lockAction = false;
				// if (this.activatedLetter !== '' && this.letterScriptTypeStore) {
				// 	this.getDataByLetter(this.activatedLetter, this.letterScriptTypeStore);
				// } else
				if (this.searchedTerm) {
					this.searchFormControl.setValue(this.searchedTerm);
					return;
				}

				switch (this.itemType) {
					case 'AUTHOR': {
						this, this.getAuthors();
						break;
					}
					case 'MEDIUM': {
						this.getMediums();
						break;
					}
					case 'TAG': {
						setTimeout(() => {
							this.getTagCategories();
							this.items = [];
							this.searchedTerm = '';
						}, this.DEBOUNCE_TIME);
						break;
					}
					case 'KEYWORD': {
						this.getAllKeywords();
						break;
					}
					case 'BUSINESS_ENTITY': {
						this.getBusinessEntities();
						break;
					}
				}
			});
	}

	searchForOneKeyword(keywordId: string) {
		const today = new Date();
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
					width: '600px',
					data: {
						mode: 'USER_ACTION',
						type: 'custom-fields',
						fields: [
							{
								type: 'date-range',
								data: [
									{
										name: '',
										value: [today, today],
									},
								],
							},
						],
					},
				});

				dialogRef
					.afterClosed()
					.pipe(take(1))
					.subscribe(result => {
						if (result && result.data) {
							const dateFrom = result.data.dateFrom || today;
							const dateTo = result.data.dateTo || today;

							const searchRequest: SearchByKeywordRequest = {
								keywordId: keywordId,
								dateFrom: dateFrom,
								dateTo: dateTo
							};

							this.apiClippingClip.searchNewsSourceForKeyword(searchRequest)
								.pipe(take(1))
								.subscribe();
						}
					});
	}

	search() {
		this.searchedTerm = this.searchFormControl.value;
		this.foundResults = 0;

		if (this.searchedTerm) {
			this.activatedLetter = '';
		} else {
			this.searchedTerm = '';
		}

		switch (this.itemType) {
			case 'AUTHOR': {
				if (this.searchedTerm) {
					this.searchAuthors();
				} else {
					this.items = this.activatedLetter ? this.items : [];
					this.getAuthors();
				}
				break;
			}
			case 'MEDIUM': {
				const hasTypeFilter = this.mediumTypeFormControl.value && this.mediumTypeFormControl.value.length > 0 && !this.mediumTypeFormControl.value.includes('ALL');
				if (this.searchedTerm || hasTypeFilter) {
					this.searchMediums();
				} else {
					this.items = this.activatedLetter ? this.items : [];
					this.getMediums();
				}
				break;
			}
			case 'TAG': {
				if (this.searchedTerm) {
					this.searchTags();
				} else {
					this.getTagCategories();
				}
				break;
			}
			case 'KEYWORD': {
				if (this.searchedTerm) {
					this.searchKeywords();
				} else {
					this.getAllKeywords();
				}
				break;
			}
			case 'BUSINESS_ENTITY': {
				if (this.searchedTerm) {
					this.searchBusinessEntities();
				} else {
					this.getBusinessEntities();
				}
			}
		}
	}

	mediumTypeSelection(itemSelected?: Event) {
		if (itemSelected) {
			if (this.selectAllMediumTypes.selected) {
				this.selectAllMediumTypes.deselect();
			} else if (this.mediumTypeFormControl.value.length === this.clipTypes.length) {
				this.selectAllMediumTypes.select();
			}
		} else {
			this.mediumTypeFormControl.patchValue(this.selectAllMediumTypes.selected ? ['ALL', ...this.clipTypes.map(item => item.id)] : []);
		}
	}

	private getSelectedMediumTypes(): ManagementMediumFilterRequest.TypesEnum[] | undefined {
		const selectedTypes = this.mediumTypeFormControl.value?.filter((type: string) => type !== 'ALL') || [];
		return selectedTypes.length > 0 ? selectedTypes as ManagementMediumFilterRequest.TypesEnum[] : undefined;
	}

	private getSelectedLangs(): ManagementMediumFilterRequest.LangsEnum[] | undefined {
		const selectedLangs = this.mediumLangFormControl.value || [];
		return selectedLangs.length > 0 ? selectedLangs as ManagementMediumFilterRequest.LangsEnum[] : undefined;
	}

	getLangCode(lang: any): string {
		return lang.langCode || lang.countryCode;
	}

	private getActiveMediumControler() {
		return this.itemTypeWithScope === 'medium_clipping' ? this.apiClippingMedium : this.apiContractNoticesMedium;
	}

	private getActiveCategoryControler() {
		switch (this.itemTypeWithScope) {
			case 'tag_clipping':
				return this.apiClippingCategory as any;
			case 'tag_contract_notices':
				return this.apiContractNoticeManagementCategory as any;
			case 'tag_transcriptions':
				return this.apiTranscriptionCategory as any;
			default:
				return this.apiClippingCategory as any;
		}
	}

	//** Api calls */
	// getDataByLetter(letter: string, scriptType: 'latin' | 'cyrillic') {
	// 	this.searchFormControl.setValue('');
	// 	setTimeout(() => {
	// 		this.activatedLetter = letter;
	// 		this.letterScriptTypeStore = scriptType;
	// 		this.foundResults = 0;
	// 		switch (this.itemType) {
	// 			case 'AUTHOR': {
	// 				this.apiClippingAuthor
	// 					.getAuthors(letter, scriptType === 'cyrillic')
	// 					.pipe(take(1))
	// 					.subscribe({
	// 						next: response => {
	// 							this.items = response;
	// 							this.foundResults = response.length;
	// 						},
	// 						error: error => {
	// 							// handle error
	// 						},
	// 					});
	// 				break;
	// 			}
	// 			case 'MEDIUM': {
	// 				this.getActiveMediumControler()
	// 					.getMediums(letter, scriptType === 'cyrillic')
	// 					.pipe(take(1))
	// 					.subscribe({
	// 						next: response => {
	// 							this.items = response;
	// 							this.foundResults = response.length;
	// 						},
	// 						error: error => {
	// 							// handle error
	// 						},
	// 					});
	// 				break;
	// 			}
	// 		}
	// 	}, this.DEBOUNCE_TIME);
	// }

	getAuthors() {
		const pagination = {
			index: this.paginator.toArray()[0] && this.paginator.toArray()[0].pageIndex ? this.paginator.toArray()[0].pageIndex : 0,
			pageSize: this.paginator.toArray()[0] && this.paginator.toArray()[0].pageSize ? this.paginator.toArray()[0].pageSize : this.pageOptions[0],
		};

		this.apiClippingAuthor
			.getAuthorsPageable(pagination.index, pagination.pageSize)
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.items = response.content || [];
					this.foundResults = response.totalElements || 0;
					if (this.paginator.toArray()[0]) {
						this.paginator.toArray()[0].length = response.totalElements || 0;
					}
				},
			});
	}

	getMediums() {
		const pagination = {
			index: this.paginator.toArray()[0] && this.paginator.toArray()[0].pageIndex ? this.paginator.toArray()[0].pageIndex : 0,
			pageSize: this.paginator.toArray()[0] && this.paginator.toArray()[0].pageSize ? this.paginator.toArray()[0].pageSize : this.pageOptions[0],
		};

		if (this.itemTypeWithScope === 'medium_clipping') {
			const filterRequest: ManagementMediumFilterRequest = {
				name: this.searchedTerm || undefined,
				types: this.getSelectedMediumTypes(),
				langs: this.getSelectedLangs()
			};
			
			(this.apiClippingMedium)
				.getMediumsPageableFilter(filterRequest, pagination.index, pagination.pageSize)
				.pipe(take(1))
				.subscribe({
					next: response => {
						this.items = response.content || [];
						this.foundResults = response.totalElements || 0;
						if (this.paginator.toArray()[0]) {
							this.paginator.toArray()[0].length = response.totalElements || 0;
						}
					},
				});
		} else {
			this.getActiveMediumControler()
				.getMediumsPageable(pagination.index, pagination.pageSize)
				.pipe(take(1))
				.subscribe({
					next: response => {
						this.items = response.content || [];
						this.foundResults = response.totalElements || 0;
						if (this.paginator.toArray()[0]) {
							this.paginator.toArray()[0].length = response.totalElements || 0;
						}
					},
				});
		}
	}

	private getTagCategories() {
		if (this.itemTypeWithScope === 'tag_transcriptions') {
			this.getActiveCategoryControler()
				.getCategories()
				.pipe(take(1))
				.subscribe({
					next: (response: TranscriptionCategoryResponse[]) => {
						const categoriesWithTags = response.map(category => ({
							...category,
							tags: [],
							isOpened: false
						})) as ManagementCategoryWithTags[];

						this.items = categoriesWithTags;
						this.accordion?.closeAll();
						this.searchStore = categoriesWithTags;

						categoriesWithTags.forEach(category => {
							this.apiTranscriptionTag.getTags(category.id!)
								.pipe(take(1))
								.subscribe({
									next: (tags: TranscriptionTagResponse[]) => {
										category.tags = tags as ManagementTagResponse[];
									}
								});
						});
					},
					error: () => {
						this.items = [];
					},
				});
		} else {
			this.getActiveCategoryControler()
				.getCategoriesWithTagsForClientMgmt()
				.pipe(take(1))
				.subscribe({
					next: (response: any) => {
						this.items = response || [];
						this.accordion?.closeAll();
						this.searchStore = response;
					},
					error: () => {
						this.items = [];
					},
				});
		}
	}

	private parseTagSearchResut(searchResults: ManagementTagResponse[]) {
		let category;
		(this.items as ManagementCategoryWithTags[]).map(categoryItem => {
			// clear stored tags in categories
			categoryItem.tags = [];
		});

		searchResults.forEach(tag => {
			category = (this.items as ManagementCategoryWithTags[]).find(category => category.id === tag.categoryId);
			if (category) {
				category.tags?.push(tag);
			}
		});
		this.accordion?.openAll();
	}

	private getAllKeywords() {
		this.apiClippingKeyword
			.getAllKeywords(0, 1000)
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.items = response.content || [];
					this.applyKeywordFilters();
				},
			});
	}

	private getKeywordTagCategories() {
		this.apiClippingCategory
			.getCategoriesWithTagsForClientMgmt()
			.pipe(take(1))
			.subscribe({
				next: (response: any) => {
					this.keywordTagCategories = response || [];
				},
				error: () => {
					this.keywordTagCategories = [];
				},
			});
	}

	toggleKeywordTagFilter(event: MatCheckboxChange, tagId: string) {
		if (event && event.checked) {
			if (!this.selectedKeywordTagIds.includes(tagId)) {
				this.selectedKeywordTagIds.push(tagId);
			}
		} else {
			this.selectedKeywordTagIds = this.selectedKeywordTagIds.filter(id => id !== tagId);
		}
		this.applyKeywordFilters();
	}

	toggleKeywordSourceFilter(event: MatCheckboxChange, source: string) {
		if (event && event.checked) {
			if (!this.selectedKeywordSources.includes(source)) {
				this.selectedKeywordSources.push(source);
			}
		} else {
			this.selectedKeywordSources = this.selectedKeywordSources.filter(item => item !== source);
		}
		this.applyKeywordFilters();
	}

	clearKeywordFilters() {
		this.selectedKeywordTagIds = [];
		this.selectedKeywordSources = [];
		this.applyKeywordFilters();
	}

	private applyKeywordFilters() {
		const keywords = (this.items as ManagementKeywordResponse[]) || [];

		const filtered = keywords.filter(keyword => {
			const tagsMatch = this.selectedKeywordTagIds.length === 0 || (keyword.tags || []).some(tag => this.selectedKeywordTagIds.includes(tag.id!));
			const sourcesMatch = this.selectedKeywordSources.length === 0 || (keyword.sources || []).some(source => this.selectedKeywordSources.includes(source));
			return tagsMatch && sourcesMatch;
		});

		this.dataSource = new MatTableDataSource<any>(filtered);
	}

	private getBusinessEntities() {
		const pagination = {
			index: this.paginator.toArray()[0] && this.paginator.toArray()[0].pageIndex ? this.paginator.toArray()[0].pageIndex : 0,
			pageSize: this.paginator.toArray()[0] && this.paginator.toArray()[0].pageSize ? this.paginator.toArray()[0].pageSize : this.pageOptions[0],
		};

		this.apiContractNoticesBusinessEntity
			.getBusinessEntitiesPageable({}, pagination.index, pagination.pageSize)
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.items = response.content || [];
					this.foundResults = response.totalElements || 0;
					if (this.paginator.toArray()[0]) {
						this.paginator.toArray()[0].length = response.totalElements || 0;
					}
				},
			});
	}

	private searchAuthors() {
		this.apiClippingAuthor
			.searchAuthors(this.searchedTerm)
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.items = response;
					this.foundResults = response.length;
				},
				error: () => {
					this.items = [];
					this.searchedTerm = '';
				},
			});
	}

	private searchMediums() {
		if (this.itemTypeWithScope === 'medium_clipping') {
			const filterRequest: ManagementMediumFilterRequest = {
				name: this.searchedTerm || undefined,
				types: this.mediumTypeFormControl.value && this.mediumTypeFormControl.value.length > 0 && !this.mediumTypeFormControl.value.includes('ALL') 
					? this.mediumTypeFormControl.value 
					: undefined,
				langs: this.getSelectedLangs()
			};
			
			(this.apiClippingMedium)
				.getMediumsPageableFilter(filterRequest, 0, 200)
				.pipe(take(1))
				.subscribe({
					next: response => {
						this.items = response.content || [];
						this.foundResults = response.totalElements || 0;
					},
					error: () => {
						this.items = this.activatedLetter ? this.items : [];
						this.searchedTerm = '';
					},
				});
		} else {
			this.getActiveMediumControler()
				.searchMediums(this.searchedTerm)
				.pipe(take(1))
				.subscribe({
					next: response => {
						this.items = response;
						this.foundResults = response.length;
					},
					error: () => {
						this.items = this.activatedLetter ? this.items : [];
						this.searchedTerm = '';
					},
				});
		}
	}

	private searchTags() {
		this.searchedTerm = this.searchFormControl.value;
		this.items = JSON.parse(JSON.stringify(this.searchStore));
		if (this.searchedTerm) {
			let matchedTags: any[] = [];
			(this.items as ManagementCategoryWithTags[])?.forEach(categoryItem => {
				matchedTags = [];
				categoryItem.isOpened = false;
				categoryItem.tags?.forEach(tag => {
					if (tag.value?.toLowerCase().includes(this.searchedTerm.toLowerCase())) {
						matchedTags.push(tag);
						categoryItem.isOpened = true;
					}
					categoryItem.tags = matchedTags;
				});
			});
		} else {
			this.accordion?.closeAll();
			this.searchedTerm = '';
		}
	}

	accordionAction(action: 'openAll' | 'closeAll' | 'open' | 'close', item?: ManagementCategoryWithTags) {
		switch (action) {
			case 'openAll':
			case 'closeAll': {
				action === 'openAll' ? this.accordion?.openAll() : this.accordion?.closeAll();
				this.items?.map((item: any) => (item.isOpened = action === 'openAll'));
				break;
			}
			case 'open':
			case 'close': {
				if (item) {
					item.isOpened = action === 'open';
				}
				break;
			}
		}
	}

	private searchKeywords() {
		this.apiClippingKeyword
			.searchKeywords(this.searchedTerm)
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.items = response;
					this.foundResults = response.length;
					this.applyKeywordFilters();
				},
				error: () => {
					this.items = [];
					this.searchedTerm = '';
				},
			});
	}

	private searchBusinessEntities() {
		this.apiContractNoticesBusinessEntity
			.getBusinessEntitiesPageable({ businessEntity: this.searchedTerm }, 0, 100)
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.items = response.content || [];
					this.foundResults = this.items.length;
				},
				error: () => {
					this.items = [];
					this.searchedTerm = '';
				},
			});
	}
}
